import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Button,
  Spin,
  Col,
  Modal,
  Input,
} from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { onError } from 'utils/handlers';
import Select from 'components/Select';
import FormSubmitControls from 'components/FormSubmitControls';
import Table from 'components/Table';
import NumericInput from 'components/NumericInput';
import ModalDelete from 'components/ModalDelete';
import {
  deleteAnuncio,
  getAnuncios,
  patchAnuncio,
  postAnuncio,
} from 'api/empresas/anuncios';

export const permissionConfig = {
  title: 'Anuncios',
  model: 'anuncio',
};

const Anuncios = ({
  selectedRowKey,
  empresa,
  mutable,
  permission,
}) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    const _data = await getAnuncios({ empresa: selectedRowKey });
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const _fetchData = async () => {
      setLoading(true);
      const _data = selectedRowKey ? await getAnuncios({ empresa: selectedRowKey }) : [];
      if (mounted) {
        setData(_data);
        setLoading(false);
      }
    };
    _fetchData();
    return () => { mounted = false; };
  }, [selectedRowKey]);

  const onCancel = () => {
    form.resetFields();
    setVisible(false);
    setSelectedRowKeys([]);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      values[empresa ? 'empresa' : 'sucursal'] = selectedRowKey;
      const [key] = selectedRowKeys;
      const saved = await (key ? patchAnuncio(key, values) : postAnuncio(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const onClickEdit = () => {
    setVisible(true);
  };

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const deleted = await deleteAnuncio(key);
    if (deleted) {
      setVisibleAlert(false);
      onCancel();
      await fetchData();
    }
    setLoading(false);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Es luminoso',
      key: 'es_luminoso',
      dataIndex: 'es_luminoso',
      width: 300,
      render: (val) => (val ? 'Si' : 'No'),
    },
    {
      titleText: 'Medida Total',
      key: 'medida_total',
      dataIndex: 'medida_total',
      width: 150,
      render: (val) => `${val} Metros Cuadrados`,
    },
  ];

  return (
    <>
      <Spin tip="Cargando..." spinning={loading}>
        <br />
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickDelete,
            onClickEdit,
          }}
          noControls={!mutable}
          allowSort={false}
          allowSearch={false}
          rowKey="id"
          permission={permission}
        />
        <Modal
          open={visible}
          closable={false}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Anuncio`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              permission={permission}
            />
          )}
          keyboard={!loading}
          maskClosable={!loading}
          onCancel={onCancel}
          forceRender
        >
          <Col span={24}>
            <Form
              form={form}
              layout="vertical"
              onFinish={() => onFinish()}
            >
              <Col span={24}>
                <Col span={24}>
                  <Form.Item
                    name="es_luminoso"
                    label="¿Es Luminoso?"
                  >
                    <Select trueFalse />
                  </Form.Item>
                </Col>
                <NumericInput
                  name="medida_total"
                  label="Medida Total"
                  required
                  tooltip={{
                    title: 'Medida en Metros cuadrados del anuncio',
                    icon: <QuestionCircleOutlined />,
                  }}
                />
              </Col>
              <Form.Item name="id" hidden>
                <Input />
              </Form.Item>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Form>
          </Col>
        </Modal>
      </Spin>
      <ModalDelete
        onDelete={deleteItem}
        onCancel={() => setVisibleAlert(false)}
        visible={visibleAlert}
        content="el anuncio seleccionado"
      />
    </>
  );
};

Anuncios.propTypes = {
  selectedRowKey: PropTypes.number,
  mutable: PropTypes.bool,
  empresa: PropTypes.bool,
  permission: PropTypes.shape({
    add: PropTypes.bool.isRequired,
    authorize: PropTypes.bool.isRequired,
    cancel: PropTypes.bool.isRequired,
    change: PropTypes.bool.isRequired,
    delete: PropTypes.bool.isRequired,
    export: PropTypes.bool.isRequired,
    import: PropTypes.bool.isRequired,
    sync: PropTypes.bool.isRequired,
    view: PropTypes.bool.isRequired,
  }).isRequired,
};

Anuncios.defaultProps = {
  empresa: true,
  selectedRowKey: null,
  mutable: true,
};

export default Anuncios;
